<template>
  <v-container
    class="report-dashboard staffing-dashboard py-0"
    fluid
  >
    <DashboardFilters @input="setCriteria" />
    <v-progress-linear
      :active="loading"
      color="secondary"
      indeterminate
    />
    <div
      v-if="dashboard"
      class="dashboard-content"
      :style="{ height: contentHeight }"
    >
      <div class="section-title">
        {{ $t('labels.hoursAndOvertime') }}
      </div>
      <v-row dense>
        <v-col
          v-for="tile in hoursTiles"
          :key="tile.key"
          cols="6"
          md="3"
        >
          <DashboardTile
            :class="tile.key"
            :detail="tile.detail"
            :label="tile.label"
            :value="tile.value"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col
          cols="12"
          md="7"
        >
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.hoursPerDay') }}
            </div>
            <BarChart
              :chart-data="hoursPerDayData"
              :options="stackedBarOptions"
              :styles="chartStyles"
            />
          </v-card>
        </v-col>
        <v-col
          cols="12"
          md="5"
        >
          <v-card
            class="chart-card"
            outlined
          >
            <div class="d-flex align-center">
              <div class="chart-title">
                {{ $t('labels.hoursBy') }}
              </div>
              <v-spacer />
              <v-btn-toggle
                v-model="breakdown"
                class="breakdown"
                color="secondary"
                dense
                mandatory
              >
                <v-btn
                  v-for="option in breakdownOptions"
                  :key="option.value"
                  small
                  text
                  :value="option.value"
                >
                  {{ option.text }}
                </v-btn>
              </v-btn-toggle>
            </div>
            <HorizontalBarChart
              :chart-data="breakdownData"
              :options="horizontalStackedBarOptions"
              :styles="barChartStyles(breakdownRows.length)"
            />
          </v-card>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.mostOvertime') }}
            </div>
            <v-simple-table
              class="top-overtime"
              dense
            >
              <thead>
                <tr>
                  <th>{{ $t('labels.employeeName') }}</th>
                  <th>{{ $t('labels.homeDepartment') }}</th>
                  <th class="text-right">
                    {{ $t('labels.overtimeHours') }}
                  </th>
                  <th class="text-right">
                    {{ $t('labels.totalHours') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in dashboard.hours.topOvertime"
                  :key="row.userId"
                >
                  <td>{{ row.name }}</td>
                  <td>{{ row.homeDepartmentName }}</td>
                  <td class="text-right">
                    {{ formatNumber(row.overtime) }}
                  </td>
                  <td class="text-right">
                    {{ formatNumber(row.total) }}
                  </td>
                </tr>
                <tr v-if="dashboard.hours.topOvertime.length === 0">
                  <td
                    class="grey--text"
                    colspan="4"
                  >
                    {{ $t('labels.noOvertime') }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>

      <div class="section-title">
        {{ $t('labels.absences') }}
      </div>
      <v-row dense>
        <v-col
          v-for="tile in absenceTiles"
          :key="tile.key"
          cols="6"
        >
          <DashboardTile
            :class="tile.key"
            :detail="tile.detail"
            :label="tile.label"
            :value="tile.value"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col
          cols="12"
          md="8"
        >
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.absencesPerDay') }}
            </div>
            <BarChart
              :chart-data="absencesPerDayData"
              :options="stackedBarOptions"
              :styles="chartStyles"
            />
          </v-card>
        </v-col>
        <v-col
          v-for="chart in doughnutCharts"
          :key="chart.key"
          cols="12"
          md="4"
        >
          <v-card
            :class="['chart-card', chart.key]"
            outlined
          >
            <div class="chart-title">
              {{ chart.title }}
            </div>
            <DoughnutChart
              v-if="chart.data.labels.length > 0"
              :chart-data="chart.data"
              :options="doughnutOptions"
              :styles="chartStyles"
            />
            <div
              v-else
              class="no-data grey--text"
              :style="chartStyles"
            >
              {{ $t('labels.noRecords') }}
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.mostAbsences') }}
            </div>
            <v-simple-table
              class="top-absences"
              dense
            >
              <thead>
                <tr>
                  <th>{{ $t('labels.employeeName') }}</th>
                  <th>{{ $t('labels.homeDepartment') }}</th>
                  <th class="text-right">
                    {{ $t('labels.callOuts') }}
                  </th>
                  <th class="text-right">
                    {{ $t('labels.callOutHours') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in dashboard.absences.topAbsences"
                  :key="row.userId"
                >
                  <td>{{ row.name }}</td>
                  <td>{{ row.homeDepartmentName }}</td>
                  <td class="text-right">
                    {{ formatNumber(row.count) }}
                  </td>
                  <td class="text-right">
                    {{ formatNumber(row.hours) }}
                  </td>
                </tr>
                <tr v-if="dashboard.absences.topAbsences.length === 0">
                  <td
                    class="grey--text"
                    colspan="4"
                  >
                    {{ $t('labels.noCallOuts') }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import { BarChart, DoughnutChart, HorizontalBarChart } from '@/components/charts';
import DashboardFilters from '@/components/scheduling/reports/DashboardFilters';
import DashboardTile from '@/components/scheduling/reports/DashboardTile';
import dashboard from '@/components/scheduling/reports/dashboard';

export default {
  components: {
    BarChart,
    DashboardFilters,
    DashboardTile,
    DoughnutChart,
    HorizontalBarChart
  },
  mixins: [dashboard],
  data () {
    return {
      breakdown: this.$store.getters['account/isDepartmentManagement']() ? 'jobType' : 'department',
      retrieveAction: 'report/retrieveStaffingDashboard',
      retrieveFailMessage: 'descriptions.staffingDashboardRetrieveFail'
    };
  },
  computed: {
    absenceTiles () {
      const { absences } = this.dashboard;
      return [
        {
          key: 'call-outs',
          label: this.$t('labels.callOuts'),
          value: this.formatNumber(absences.callOuts.count),
          detail: this.$t('labels.hoursCount', { hours: this.formatNumber(absences.callOuts.hours) })
        },
        {
          key: 'canceled',
          label: this.$t('labels.canceled'),
          value: this.formatNumber(absences.canceled.count),
          detail: this.$t('labels.hoursCount', { hours: this.formatNumber(absences.canceled.hours) })
        }
      ];
    },
    absencesPerDayData () {
      const { byDate } = this.dashboard.absences;
      return {
        labels: this.dayLabels(byDate),
        datasets: [
          this.dataset(this.$t('labels.callOuts'), this.colors.error, byDate.map(day => day.callOuts)),
          this.dataset(this.$t('labels.canceled'), this.colors.primary, byDate.map(day => day.canceled))
        ]
      };
    },
    breakdownData () {
      const rows = this.breakdownRows;
      return {
        labels: rows.map(row => row.name || this.$t('labels.unknown')),
        datasets: [
          this.dataset(this.$t('labels.regular'), this.colors.secondary, rows.map(row => row.regular)),
          this.dataset(this.$t('labels.overtime'), this.colors.accent, rows.map(row => row.overtime))
        ]
      };
    },
    breakdownOptions () {
      const options = [];
      if (!this.isDepartmentManagement) {
        options.push({ text: this.$tc('labels.department', 1), value: 'department' });
      }
      options.push(
        { text: this.$tc('labels.jobType', 1), value: 'jobType' },
        { text: this.$t('labels.jobStatus'), value: 'jobStatus' }
      );
      return options;
    },
    breakdownRows () {
      const keys = {
        department: 'byDepartment',
        jobStatus: 'byJobStatus',
        jobType: 'byJobType'
      };
      return this.dashboard.hours[keys[this.breakdown]];
    },
    doughnutCharts () {
      const { absences } = this.dashboard;
      return [
        {
          key: 'call-outs-by-reason',
          title: this.$t('labels.callOutsByReason'),
          data: this.doughnutData(absences.callOutsByFlag, 'count')
        }
      ];
    },
    hoursPerDayData () {
      const { byDate } = this.dashboard.hours;
      return {
        labels: this.dayLabels(byDate),
        datasets: [
          this.dataset(this.$t('labels.regular'), this.colors.secondary, byDate.map(day => day.regular)),
          this.dataset(this.$t('labels.overtime'), this.colors.accent, byDate.map(day => day.overtime))
        ]
      };
    },
    hoursTiles () {
      const { hours } = this.dashboard;
      const overtimePercent = hours.total > 0 ? Math.round(hours.overtime / hours.total * 1000) / 10 : 0;
      return [
        {
          key: 'scheduled',
          label: this.$t('labels.scheduledHours'),
          value: this.formatNumber(hours.total),
          detail: this.$t('labels.staffCount', { count: this.formatNumber(hours.staffCount) })
        },
        {
          key: 'overtime',
          label: this.$t('labels.overtimeHours'),
          value: this.formatNumber(hours.overtime),
          detail: this.$t('labels.ofScheduledHours', { percent: overtimePercent })
        },
        {
          key: 'float',
          label: this.$t('labels.floatHours'),
          value: this.formatNumber(hours.float),
          detail: ''
        },
        {
          key: 'sitter',
          label: this.$t('labels.sitterHours'),
          value: this.formatNumber(hours.sitter),
          detail: ''
        }
      ];
    }
  }
};
</script>

<style lang="scss">
@import "src/assets/sass/report-dashboard.scss";
</style>
